import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import * as XLSX from 'xlsx';
import { renderAsync } from 'docx-preview';
import { FileWarning, LayoutDashboard, Maximize2, Minimize2 } from 'lucide-react';
import { decodeBase64, formatFileSize, type FilePreviewResponse } from '../filePreviewApi';
import { logDataModels } from '../dataModelsLogger';
import { ghostBtn, surfaces } from './dataModelsUi';

type ViewerKind = 'pdf' | 'image' | 'markdown' | 'text' | 'csv' | 'xlsx' | 'docx' | 'unknown';

export function PreviewExpandButton({
  isLight,
  expanded,
  onToggle,
}: {
  isLight: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const Icon = expanded ? Minimize2 : Maximize2;
  return (
    <button
      type="button"
      className={`${ghostBtn(isLight)} !h-8 !w-8 !px-0`}
      title={expanded ? 'Restore' : 'Maximize'}
      aria-label={expanded ? 'Restore Data Preview' : 'Maximize Data Preview'}
      onClick={onToggle}
    >
      <Icon size={14} />
    </button>
  );
}

export function previewShellClass(isLight: boolean, expanded: boolean, card: string): string {
  if (!expanded) return `rounded-2xl border p-4 ${card}`;
  return `fixed inset-3 z-50 flex min-h-0 flex-col rounded-2xl border p-4 shadow-2xl ${card} ${
    isLight ? 'bg-white' : 'bg-[#0B1220]'
  }`;
}

export default function FilePreview({
  isLight,
  file,
}: {
  isLight: boolean;
  file: FilePreviewResponse;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const card = ui.card;
  const [expanded, setExpanded] = useState(false);
  const bytes = useMemo(() => decodeBase64(file.contentBase64), [file.contentBase64]);
  const kind = useMemo(() => viewerKind(file.fileName, file.mimeType), [file.fileName, file.mimeType]);
  const blobUrl = useMemo(() => {
    if (kind !== 'image' && kind !== 'pdf') return '';
    return URL.createObjectURL(
      new Blob([toArrayBuffer(bytes)], { type: kind === 'pdf' ? 'application/pdf' : file.mimeType || 'application/octet-stream' }),
    );
  }, [bytes, file.mimeType, kind]);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  useEffect(() => {
    logDataModels('app', 'file_preview_render', { kind, bytes: bytes.byteLength || bytes.length || 0, mimeType: file.mimeType });
  }, [bytes, file.mimeType, kind]);

  usePreviewEscape(expanded, () => setExpanded(false));

  return (
    <>
      {expanded ? <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setExpanded(false)} /> : null}
      <article className={previewShellClass(isLight, expanded, card)}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className={`inline-flex items-center gap-2 text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
            <LayoutDashboard size={15} />
            Data Preview
          </h3>
          <div className="flex items-center gap-2">
            <p className={`text-xs ${muted}`}>
              {file.fileName} · {file.mimeType || 'unknown type'} · {formatFileSize(file.size || bytes.byteLength)}
            </p>
            <PreviewExpandButton isLight={isLight} expanded={expanded} onToggle={() => setExpanded((value) => !value)} />
          </div>
        </div>
        <div className={expanded ? 'min-h-0 flex-1 overflow-hidden' : ''}>
          <FileViewer
            isLight={isLight}
            kind={kind}
            file={file}
            bytes={bytes}
            blobUrl={blobUrl}
            expanded={expanded}
            truncated={file.truncated}
          />
        </div>
      </article>
    </>
  );
}

function FileViewer({
  isLight,
  kind,
  file,
  bytes,
  blobUrl,
  expanded,
  truncated,
}: {
  isLight: boolean;
  kind: ViewerKind;
  file: FilePreviewResponse;
  bytes: Uint8Array;
  blobUrl: string;
  expanded: boolean;
  truncated?: boolean;
}) {
  if (kind === 'pdf') return <PdfViewer bytes={bytes} blobUrl={blobUrl} isLight={isLight} expanded={expanded} />;
  if (kind === 'image') return <img src={blobUrl} alt={file.fileName} className={`${expanded ? 'max-h-full' : 'max-h-[70vh]'} w-full object-contain`} />;
  if (kind === 'markdown') return <MarkdownViewer text={decodeText(bytes, truncated)} isLight={isLight} expanded={expanded} />;
  if (kind === 'text') return <TextViewer text={decodeText(bytes, truncated)} isLight={isLight} expanded={expanded} />;
  if (kind === 'csv') {
    return (
      <CsvViewer
        text={decodeText(bytes, truncated)}
        delimiter={file.fileName.toLowerCase().endsWith('.tsv') ? '\t' : ','}
        isLight={isLight}
        expanded={expanded}
      />
    );
  }
  if (kind === 'xlsx') return <XlsxViewer bytes={bytes} isLight={isLight} expanded={expanded} />;
  if (kind === 'docx') return <DocxViewer bytes={bytes} isLight={isLight} expanded={expanded} />;
  return <UnknownViewer file={file} size={bytes.byteLength} isLight={isLight} />;
}

function PdfViewer({
  bytes,
  blobUrl,
  isLight,
  expanded,
}: {
  bytes: Uint8Array;
  blobUrl: string;
  isLight: boolean;
  expanded: boolean;
}) {
  const muted = surfaces(isLight).muted;
  const length = bytes.byteLength || bytes.length || 0;
  useEffect(() => {
    logDataModels(length ? 'app' : 'warning', 'file_preview_pdf', { bytes: length, hasBlob: Boolean(blobUrl) });
  }, [blobUrl, length]);
  if (!length || !blobUrl) {
    return <p className={`px-3 py-8 text-center text-sm ${muted}`}>This PDF has no content.</p>;
  }
  return (
    <iframe
      title="PDF preview"
      src={blobUrl}
      className={`w-full border-0 ${expanded ? 'h-full min-h-[70vh]' : 'h-[70vh]'} rounded-xl border ${
        isLight ? 'border-[#E6E8EC] bg-[#F7F8FA]' : 'border-white/10 bg-black/20'
      }`}
    />
  );
}

function MarkdownViewer({ text, isLight, expanded }: { text: string; isLight: boolean; expanded: boolean }) {
  return (
    <div
      className={`${expanded ? 'h-full' : ''} prose max-w-none overflow-auto rounded-xl border p-4 ${
        isLight ? 'border-[#E6E8EC] prose-neutral' : 'border-white/10 prose-invert'
      }`}
    >
      <ReactMarkdown>{text}</ReactMarkdown>
    </div>
  );
}

function TextViewer({ text, isLight, expanded }: { text: string; isLight: boolean; expanded: boolean }) {
  return (
    <pre
      className={`${expanded ? 'h-full' : 'max-h-[70vh]'} overflow-auto rounded-xl border p-3 font-mono text-[12.5px] leading-5 ${
        isLight ? 'border-[#E6E8EC] bg-white text-[#1C1C1C]' : 'border-white/10 bg-black text-slate-100'
      }`}
    >
      {text}
    </pre>
  );
}

function CsvViewer({
  text,
  delimiter,
  isLight,
  expanded,
}: {
  text: string;
  delimiter: string;
  isLight: boolean;
  expanded: boolean;
}) {
  const rows = useMemo(() => parseDelimited(text, delimiter), [delimiter, text]);
  return <ReadonlyGrid rows={rows} isLight={isLight} expanded={expanded} enableFilter />;
}

function XlsxViewer({ bytes, isLight, expanded }: { bytes: Uint8Array; isLight: boolean; expanded: boolean }) {
  const workbook = useMemo(() => XLSX.read(bytes, { type: 'array', cellDates: true }), [bytes]);
  const sheets = workbook.SheetNames.filter(Boolean);
  const [active, setActive] = useState(sheets[0] || '');
  useEffect(() => {
    setActive((current) => (sheets.includes(current) ? current : sheets[0] || ''));
  }, [bytes, workbook]);
  const rows = useMemo(() => {
    const sheet = workbook.Sheets[active];
    if (!sheet) return [];
    return XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '', blankrows: false }) as unknown[][];
  }, [active, workbook]);
  const muted = surfaces(isLight).muted;
  if (!sheets.length) {
    return <p className={`px-3 py-8 text-center text-sm ${muted}`}>This workbook has no sheets.</p>;
  }
  return (
    <div className={`${expanded ? 'flex h-full min-h-0 flex-col' : ''} space-y-3`}>
      <div className="flex flex-wrap gap-1">
        {sheets.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => setActive(name)}
            className={`rounded-md px-2.5 py-1 text-xs ${
              name === active
                ? isLight
                  ? 'bg-[#1B6CA8] text-white'
                  : 'bg-sky-400 text-slate-950'
                : isLight
                  ? 'bg-[#F2F4F7] text-[#4B5563]'
                  : 'bg-white/10 text-slate-300'
            }`}
          >
            {name}
          </button>
        ))}
      </div>
      <ReadonlyGrid key={active} rows={rows} isLight={isLight} expanded={expanded} enableFilter />
    </div>
  );
}

function DocxViewer({ bytes, isLight, expanded }: { bytes: Uint8Array; isLight: boolean; expanded: boolean }) {
  const host = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    const node = host.current;
    if (!node) return undefined;
    node.innerHTML = '';
    renderAsync(toArrayBuffer(bytes), node).catch((err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unable to render this document';
      logDataModels('error', 'file_preview_docx_failed', { message });
      setError(message);
    });
    return () => {
      node.innerHTML = '';
    };
  }, [bytes]);
  if (error) {
    return <p className={`px-3 py-8 text-center text-sm ${surfaces(isLight).muted}`}>{error}</p>;
  }
  return (
    <div
      ref={host}
      className={`${expanded ? 'h-full' : 'max-h-[70vh]'} overflow-auto rounded-xl border p-4 ${
        isLight ? 'border-[#E6E8EC] bg-white' : 'border-white/10 bg-white text-neutral-900'
      }`}
    />
  );
}

function UnknownViewer({ file, size, isLight }: { file: FilePreviewResponse; size: number; isLight: boolean }) {
  const muted = surfaces(isLight).muted;
  return (
    <div className={`flex flex-col items-center gap-2 px-3 py-12 text-center ${muted}`}>
      <FileWarning size={22} />
      <p className={`text-sm font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>Preview not available</p>
      <p className="text-xs">
        {file.fileName} · {file.mimeType || 'unknown type'} · {formatFileSize(file.size || size)}
      </p>
    </div>
  );
}

function ReadonlyGrid({
  rows,
  isLight,
  expanded,
  enableFilter,
}: {
  rows: unknown[][];
  isLight: boolean;
  expanded: boolean;
  enableFilter?: boolean;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const border = ui.border;
  const [filters, setFilters] = useState<Record<number, string>>({});
  const headers = (rows[0] || []).map((cell, index) => String(cell ?? `Column ${index + 1}`));
  const body = rows.slice(1);
  const filtered = useMemo(() => {
    const needles = Object.entries(filters)
      .map(([index, value]) => ({ index: Number(index), value: value.trim().toLowerCase() }))
      .filter((item) => item.value);
    if (!needles.length) return body;
    return body.filter((row) =>
      needles.every((item) => String(row[item.index] ?? '').toLowerCase().includes(item.value)),
    );
  }, [body, filters]);
  if (!headers.length) {
    return <p className={`px-3 py-8 text-center text-sm ${muted}`}>This file has no rows.</p>;
  }
  return (
    <div
      className={`${expanded ? 'min-h-0 flex-1' : 'max-h-[70vh]'} overflow-auto rounded-xl border ${border}`}
    >
      <table className="min-w-full font-mono text-[12.5px]">
        <thead className={`sticky top-0 z-10 ${ui.tableHead}`}>
          <tr className="text-left text-[11px] font-semibold uppercase tracking-wider">
            {headers.map((header, index) => (
              <th key={`${header}-${index}`} className="px-3 py-2">
                {header}
              </th>
            ))}
          </tr>
          {enableFilter ? (
            <tr>
              {headers.map((header, index) => (
                <th key={`filter-${header}-${index}`} className="px-2 pb-2">
                  <input
                    value={filters[index] || ''}
                    onChange={(event) => {
                      const value = event.target.value;
                      setFilters((current) => {
                        const next = { ...current };
                        if (value) next[index] = value;
                        else delete next[index];
                        return next;
                      });
                    }}
                    placeholder="Filter"
                    className={`h-7 w-full min-w-[6rem] rounded border px-2 text-[11px] font-normal normal-case tracking-normal outline-none ${
                      isLight
                        ? 'border-[#D8DCE2] bg-white text-[#1C1C1C] placeholder:text-[#9AA1AB]'
                        : 'border-white/15 bg-black text-slate-100 placeholder:text-slate-500'
                    }`}
                  />
                </th>
              ))}
            </tr>
          ) : null}
        </thead>
        <tbody>
          {filtered.map((row, rowIndex) => (
            <tr key={rowIndex} className={`border-t ${border}`}>
              {headers.map((_, colIndex) => (
                <td key={colIndex} className={`whitespace-nowrap px-3 py-2 ${isLight ? 'text-neutral-800' : 'text-neutral-200'}`}>
                  {String(row[colIndex] ?? '')}
                </td>
              ))}
            </tr>
          ))}
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className={`px-3 py-8 text-center text-sm ${muted}`}>
                No rows match the current filters.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function usePreviewEscape(expanded: boolean, onRestore: () => void) {
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onRestore();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded, onRestore]);
}

function viewerKind(fileName: string, mimeType: string): ViewerKind {
  const mime = (mimeType || '').toLowerCase();
  const name = (fileName || '').toLowerCase();
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)) return 'image';
  if (mime.includes('markdown') || name.endsWith('.md')) return 'markdown';
  if (mime.includes('spreadsheet') || /\.(xlsx|xlsm|xls)$/.test(name)) return 'xlsx';
  if (mime.includes('wordprocessingml') || name.endsWith('.docx')) return 'docx';
  if (mime.includes('csv') || name.endsWith('.csv')) return 'csv';
  if (name.endsWith('.tsv')) return 'csv';
  if (
    mime.startsWith('text/') ||
    mime.includes('json') ||
    mime.includes('xml') ||
    /\.(txt|json|xml|html|htm|log)$/.test(name)
  ) {
    return 'text';
  }
  return 'unknown';
}

function decodeText(bytes: Uint8Array, truncated?: boolean): string {
  try {
    const text = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    if (!truncated) return text;
    const lastBreak = Math.max(text.lastIndexOf('\n'), text.lastIndexOf('\r'));
    return lastBreak > 0 ? text.slice(0, lastBreak) : text;
  } catch {
    return '';
  }
}

function parseDelimited(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let current = '';
  let row: string[] = [];
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === '"') {
      if (quoted && text[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (char === delimiter && !quoted) {
      row.push(current);
      current = '';
      continue;
    }
    if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      row.push(current);
      rows.push(row);
      row = [];
      current = '';
      continue;
    }
    current += char;
  }
  if (current || row.length) {
    row.push(current);
    rows.push(row);
  }
  return rows.filter((item) => item.some((cell) => cell.trim()));
}

function copyBytes(bytes: Uint8Array): Uint8Array {
  const length = bytes.byteLength || bytes.length || 0;
  const copy = new Uint8Array(length);
  copy.set(bytes);
  return copy;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return copyBytes(bytes).buffer;
}
